import React, { useRef, useEffect } from 'react';
import './SkillCard.css';

const SkillCard = ({
                       skill,
                       spCost,
                       getActionCost,
                       locked = false,
                       expanded = false,
                       onToggleDescription,
                       onIncrement,
                       onDecrement,
                       onRemove,
                       description
                   }) => {
    // Auto-granted skills are locked: no buying, selling, or removing.
    const canDecrement = !locked && (skill.primaryBaseCost !== 0 || skill.skillset !== 8);
    const isSinglePurchase = skill && !(skill.incrementCost > 0);

    // Group-aware so the label always matches the change in the SP total: for
    // slotted skills, dropping/adding a rank can re-tier the rest of the group.
    const refund = getActionCost(skill, skill.ranks) - getActionCost(skill, skill.ranks - 1);
    const increment = !isSinglePurchase
        ? getActionCost(skill, skill.ranks + 1) - getActionCost(skill, skill.ranks)
        : 0;
    const incrementLabel = !isSinglePurchase ? `${increment} SP` : 'Max';

    const nameRef = useRef();

    // Auto-fit the skill name so it shrinks instead of showing an ellipsis.
    const adjustFontSize = () => {
        const el = nameRef.current;
        if (!el) return;
        el.style.fontSize = '';
        const fontSize = parseFloat(getComputedStyle(el).fontSize);
        const minFontSize = 8;
        // Leave a couple px of slack so sub-pixel rounding doesn't leave the text
        // 1px too wide (which would still trigger the ellipsis).
        const available = el.clientWidth - 2;

        if (available > 0 && el.scrollWidth > available) {
            const scale = available / el.scrollWidth;
            const newSize = Math.max(minFontSize, fontSize * scale);
            el.style.fontSize = newSize + 'px';
        }
    };

    // Re-fit only when the name or the viewport changes — NOT when the SP cost /
    // ranks change. The controls are a fixed width, so the name's available width
    // is rank-independent; re-measuring on every rank change is what made the
    // font "walk" smaller/larger as ranks were added and removed.
    useEffect(() => {
        adjustFontSize();
        window.addEventListener('resize', adjustFontSize);
        // Re-fit once after web fonts load, since text metrics change then.
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(adjustFontSize).catch(() => {});
        }
        return () => {
            window.removeEventListener('resize', adjustFontSize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skill.name]);

    const isControlButton = (el) => {
        if (!el) return false;
        return el.closest('.control-btn') !== null;
    };

    // Left click / tap anywhere except the control buttons toggles the description.
    const handleCardClick = (e) => {
        if (isControlButton(e.target)) return;
        if (onToggleDescription) onToggleDescription(skill.skillId);
    };

    return (
        <div
            className={`skill-card${expanded ? ' skill-card--expanded' : ''}`}
            onClick={handleCardClick}
        >
            <div className="skill-line">
                <button
                    type="button"
                    className="control-btn remove-skill-btn"
                    onClick={(e) => onRemove(skill.skillId, e)}
                    disabled={locked}
                    title={locked ? "This skill is granted automatically and can't be removed." : "Remove skill"}
                    aria-label="Remove skill"
                >
                    ✕
                </button>
                <div className="skill-name" ref={nameRef}>
                    {skill.name || skill.skillId}
                </div>
                <div className="skill-controls-horizontal">
                    <div className="skill-rank-controls">
                        {/* Always rendered (even when empty) so single- and
                            multi-purchase skill names stay vertically aligned. */}
                        <span className="skill-ranks">
                            {!isSinglePurchase && (
                                <><span className="rank-x">x</span>{skill.ranks}</>
                            )}
                        </span>
                        <div className="skill-cost">{spCost} SP</div>
                        <button
                            type="button"
                            className="control-btn decrement-btn"
                            onClick={(e) => onDecrement(skill.skillId, e)}
                            disabled={!canDecrement}
                        >
                            <span className="control-btn-down-arrow">↓</span> {refund} SP
                        </button>
                        <button
                            type="button"
                            className="control-btn increment-btn"
                            onClick={(e) => onIncrement(skill.skillId, e)}
                            disabled={isSinglePurchase || locked}
                            title={isSinglePurchase ? "This is a single-purchase skill. Max rank reached." : ""}
                        >
                            <span className="control-btn-down-arrow">↑</span> {incrementLabel}
                        </button>
                    </div>
                </div>
            </div>

            {expanded && (
                <div
                    className="skill-desc-inline"
                    dangerouslySetInnerHTML={{
                        __html: description || 'No description available.'
                    }}
                />
            )}
        </div>
    );
};

export default SkillCard;
